import styled from 'styled-components';
import React from 'react';
import { TCloudProject } from 'containers/user/CloudManagementModalContainer';
import InfiniteScroll from 'react-infinite-scroll-component';
import CircularLoading from 'components/base/loading/CircularLoading';
import ListEmpty from 'components/common/list/ListEmpty';
import CustomSpan from 'components/common/text/CustomSpan';
import { T } from 'components/common/text';
import MuiWrapper from 'components/common/input/MuiWrapper';
import { Checkbox, IconButton } from '@material-ui/core';
import { DateConverter } from 'components/common/convert';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@material-ui/icons/Close';
import { useCloudManageStore } from 'store/local/cloudManage';
import cx from 'classnames';

interface CloudProjectListProps {
  checkAll: boolean;
  checkedList: Set<TCloudProject>;
  cloudProjectList: TCloudProject[];
  isLoadingSuccess: boolean;
  isLoadingFailure: boolean;
  hasMoreFetchData: boolean;
  onFetchCloudProjectList: () => void;
  onRefreshList: () => void;
  onCheckAllProjects: (val: boolean) => void;
  onCheckProject: (val: TCloudProject) => void;
}

const CloudProjectList = ({
  checkAll,
  checkedList,
  cloudProjectList,
  isLoadingSuccess,
  isLoadingFailure,
  hasMoreFetchData,
  onFetchCloudProjectList,
  onRefreshList,
  onCheckAllProjects,
  onCheckProject,
}: CloudProjectListProps) => {
  const { completeDownloadList } = useCloudManageStore();

  const { t } = useTranslation();
  return (
    <StyledCloudProjectList data-component-name="CloudProjectList">
      <div className="cloudProjectList__header">
        <div className="cloudProjectList__row">
          <div className="cloudProjectList__row_item check">
            <MuiWrapper>
              <Checkbox
                checked={checkAll}
                onChange={() => {
                  onCheckAllProjects(checkAll);
                }}
                className="checkbox_control_checkbox"
                color="primary"
                icon={<div className="custom_checkbox"></div>}
              />
            </MuiWrapper>
          </div>
          <div className="cloudProjectList__row_item project">
            <CustomSpan fontSize={16} fontWeight={600}>
              <T>PROJECT_NAME</T>
            </CustomSpan>
          </div>
          <div className="cloudProjectList__row_item projectId">
            <CustomSpan fontSize={16} fontWeight={600}>
              <T>PROJECT_ID</T>
            </CustomSpan>
          </div>
          <div className="cloudProjectList__row_item size">
            <CustomSpan fontSize={16} fontWeight={600}>
              <T>GLOBAL_SIZE</T>
            </CustomSpan>
          </div>
          <div className="cloudProjectList__row_item modify">
            <CustomSpan fontSize={16} fontWeight={600}>
              <T>GLOBAL_MODIFIED_DATE</T>
            </CustomSpan>
          </div>
        </div>
      </div>
      <InfiniteScroll
        className="cloudProjectList__body"
        dataLength={cloudProjectList?.length}
        next={onFetchCloudProjectList}
        hasMore={hasMoreFetchData}
        loader={
          <div className="infinite-scroll-loading">
            <CircularLoading />
          </div>
        }
        height={595}
        scrollThreshold={0.95}
      >
        {!!isLoadingSuccess &&
          (cloudProjectList.length === 0 ? (
            <ListEmpty text="PROJECT_LIST_EMPTY" />
          ) : (
            cloudProjectList?.map((item: TCloudProject, index: number) => {
              const isChecked = checkedList.has(item);
              const idDownloaded = completeDownloadList.includes(item.projectIdx);
              return (
                <div
                  className={cx('cloudProjectList__row', {
                    idDownloaded: idDownloaded,
                  })}
                  key={index}
                >
                  <div className="cloudProjectList__row_item check">
                    <MuiWrapper>
                      <Checkbox
                        checked={isChecked}
                        className="checkbox_control_checkbox"
                        color="primary"
                        icon={<div className="custom_checkbox"></div>}
                        onChange={() => {
                          onCheckProject(item);
                        }}
                      />
                    </MuiWrapper>
                  </div>
                  <div className="cloudProjectList__row_item project">
                    <CustomSpan
                      fontSize={16}
                      fontWeight={400}
                      className="cloudProjectList__row_span"
                    >
                      {item.projectName}
                    </CustomSpan>
                  </div>
                  <div className="cloudProjectList__row_item projectId">
                    <CustomSpan
                      fontSize={16}
                      fontWeight={400}
                      fontColor={'#888'}
                      className="cloudProjectList__row_span"
                    >
                      {item.projectId}
                    </CustomSpan>
                  </div>
                  <div className="cloudProjectList__row_item size">
                    <CustomSpan fontSize={16} fontWeight={400} fontColor={'#888'}>
                      {`${item.totalSizeMB} MB`}
                    </CustomSpan>
                  </div>
                  <div className="cloudProjectList__row_item modify">
                    <CustomSpan fontSize={16} fontWeight={400} fontColor={'#888'}>
                      <DateConverter timestamp={item.modifyDate} format={'YYYY.MM.DD'} />
                    </CustomSpan>
                  </div>
                </div>
              );
            })
          ))}
        {!!isLoadingFailure && (
          <ListEmpty text="GLOBAL_LIST_ERROR" isRefreshButton={true} onRefresh={onRefreshList} />
        )}
      </InfiniteScroll>
      {[...checkedList].length > 0 && (
        <div className="cloudProjectList__checked_count_box">
          <CustomSpan fontSize={16} fontWeight={400} fontColor={'#444'}>
            {t(`SELECTED_PROJECT_COUNT`, { count: [...checkedList].length })}
          </CustomSpan>

          <IconButton
            className="cloudProjectList__checked_clear"
            onClick={() => {
              onCheckAllProjects(true);
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>
      )}
    </StyledCloudProjectList>
  );
};

const StyledCloudProjectList = styled.div<{}>`
  width: 100%;
  position: relative;
  .cloudProjectList__header {
    .cloudProjectList__row {
      border-bottom: 1px solid #e1e1e7;
    }
  }
  .cloudProjectList__body {
    display: flex;
    flex-direction: column;
    row-gap: 8px;
  }

  .cloudProjectList__row {
    display: flex;
    align-items: center;
    padding-left: 20px;
    height: 36px;
    &.idDownloaded {
      background-color: #f4f4f4;
    }
    .cloudProjectList__row_item {
      display: flex;
      align-items: center;
      &.check {
        width: 24px;
        .checkbox_control_checkbox {
          width: 24px;
          padding: 0;
        }
      }
      &.project {
        width: 320px;
        padding-left: 8px;
        margin-right: 40px;
      }
      &.projectId {
        width: 320px;
        margin-right: 40px;
      }
      &.size {
        width: 80px;
        margin-right: 40px;
      }
      &.modify {
        width: 90px;
      }

      .cloudProjectList__row_span {
        display: inline-block;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }
    }
  }

  .custom_checkbox {
    width: 18px;
    height: 18px;
    border: 1px solid #e5e7ed;
    border-radius: 4px;
  }

  .cloudProjectList__checked_count_box {
    position: absolute;
    bottom: 30px;
    left: 50%;
    transform: translate(-50%, 0);
    width: 280px;
    height: 40px;
    border-radius: 8px;
    background-color: #fff;
    box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.24);
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 10px;

    .cloudProjectList__checked_clear {
      width: 24px;
      height: 24px;
    }
  }
`;

export default CloudProjectList;
